import React, { useState } from "react";
import { useParams, useNavigate, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  Flag,
  MapPinned,
  Calendar,
  Users,
  Camera,
  AlertTriangle,
  Mountain,
} from "lucide-react";
import { fetchLandmark, type LandmarkResponse } from "../api/features";
import type { CreateCheckinResponse } from "../api/landmarks";
import { CheckinDialog, CheckinGallery, checkinGalleryKey } from "../components/PhotoCheckin";
import { landmarkCategoryIcon, landmarkCategoryLabel } from "../lib/landmark-icons";
import { useAuth } from "../hooks/useAuth";
import { usePageMeta } from "../hooks/usePageMeta";
import { JsonLd } from "../components/JsonLd";
import AppShell from "../layouts/AppShell";
import { AtlasPanel } from "../components/AtlasPanel";
import { StatBlock } from "../components/StatBlock";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { CollectionCard } from "../components/CollectionCard";
import { CollectibleItem } from "../components/CollectibleItem";
import { Skel } from "../components/Skeleton";
import { EmptyState } from "../components/EmptyState";
import { GuestTopBar } from "../components/GuestTopBar";
import { AddToListButton } from "../components/AddToListButton";
import { StarRating } from "../components/StarRating";
import { placeOf } from "../lib/place-names";

// ─── Page content ─────────────────────────────────────────────────────────────

// Categories that read as historic/heritage sites rather than a generic
// tourist attraction, for the JSON-LD @type.
const HISTORIC_CATEGORIES = new Set(["stone_circle"]);

// Exported for the /preview-landmark marketing-capture route (router.tsx).
export function LandmarkContent({ data, isAuthenticated }: { data: LandmarkResponse; isAuthenticated: boolean }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { landmark, peak, visits, collections, nearbyUncollected, rating } = data;
  const visited = visits.length > 0;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastResult, setLastResult] = useState<CreateCheckinResponse | null>(null);

  const canonicalPath = `/landmarks/${landmark.slug}`;
  const categoryLabel = landmarkCategoryLabel(landmark.category);
  // Matches placeOf() in scripts/prerender.mjs (build-time static pages) so
  // the two SEO layers agree once client JS hydrates.
  const place = placeOf(landmark.region, landmark.country);
  usePageMeta({
    title: `${landmark.name} - ${categoryLabel.toLowerCase()} in ${place} | Atlas`,
    description:
      landmark.description ??
      `${landmark.name} is a ${categoryLabel.toLowerCase()} in ${place}. Track your visit and see nearby uncollected landmarks on Atlas.`,
    canonicalPath,
  });

  function handleCheckinSuccess(result: CreateCheckinResponse) {
    setDialogOpen(false);
    setLastResult(result);
    // The check-in may unlock badges / level-ups - refetch everything that
    // could have moved; the level-up fanfare rides the usual profile refetch.
    void queryClient.invalidateQueries({ queryKey: ["landmarks", landmark.slug] });
    void queryClient.invalidateQueries({ queryKey: checkinGalleryKey({ kind: "landmark", slug: landmark.slug }) });
    // A trig pillar photographed at the top also collects its peak (spec 34).
    if (result.peakCredited) {
      void queryClient.invalidateQueries({ queryKey: ["peaks", result.peakCredited.slug] });
      void queryClient.invalidateQueries({ queryKey: ["collections"] });
      void queryClient.invalidateQueries({ queryKey: ["map"] });
      void queryClient.invalidateQueries({ queryKey: ["map/features"] });
    }
    void queryClient.invalidateQueries({ queryKey: ["achievements"] });
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  }

  return (
    <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": HISTORIC_CATEGORIES.has(landmark.category)
            ? "LandmarksOrHistoricalBuildings"
            : "TouristAttraction",
          name: landmark.name,
          description: landmark.description ?? undefined,
          url: `https://goatlas.co.uk${canonicalPath}`,
          geo: {
            "@type": "GeoCoordinates",
            latitude: landmark.latitude,
            longitude: landmark.longitude,
          },
        }}
      />
      <button
        type="button"
        onClick={() => void navigate(isAuthenticated ? { to: "/map" } : { to: "/" })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          alignSelf: "flex-start",
          background: "none",
          border: "none",
          color: "var(--text-secondary)",
          cursor: "pointer",
          padding: 0,
          fontFamily: "var(--font-sans)",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        <ChevronLeft size={18} /> {isAuthenticated ? "Map" : "Atlas"}
      </button>

      {/* hero */}
      <div
        className="atlas-topo"
        style={{
          position: "relative",
          borderRadius: "var(--radius-xl)",
          border: "1px solid var(--border-default)",
          padding: 28,
          overflow: "hidden",
          boxShadow: "var(--shadow-md)",
          flexShrink: 0,
        }}
      >
        <div style={{ position: "relative", display: "flex", alignItems: "flex-start", gap: 28, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              {[landmark.region, landmark.country].filter(Boolean).join(" · ") || "Landmark"}
            </div>
            <h1
              style={{
                font: "var(--type-display)",
                fontSize: 44,
                color: "var(--text-primary)",
                margin: "0 0 12px",
                letterSpacing: "-.02em",
              }}
            >
              {landmark.name}
            </h1>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {visited ? (
                <Badge variant="gold" dot>
                  Visited{visits.length > 1 ? ` · ${visits.length} times` : ""}
                </Badge>
              ) : (
                <Badge variant="neutral">Not yet visited</Badge>
              )}
              {collections.map((col) => (
                <Badge key={col.slug} variant="neutral">
                  {col.name}
                </Badge>
              ))}
              {peak && (
                <Link to="/peaks/$slug" params={{ slug: peak.slug }} style={{ textDecoration: "none" }}>
                  <Badge variant="neutral">
                    Stands on {peak.name} · {peak.elevationM} m
                  </Badge>
                </Link>
              )}
            </div>
            <div style={{ marginTop: 14 }}>
              <StarRating itemType="landmark" itemId={landmark.id} initial={rating} isAuthenticated={isAuthenticated} />
            </div>
            {landmark.description && (
              <p style={{ marginTop: 14, maxWidth: 560, fontSize: 14, lineHeight: 1.6, color: "var(--text-secondary)" }}>
                {landmark.description}
              </p>
            )}
            {/* Page's single Summit Gold CTA (LMK-11). */}
            {isAuthenticated && (
              <div style={{ marginTop: 16 }}>
                <Button variant="primary" onClick={() => setDialogOpen(true)}>
                  <Camera size={15} style={{ marginRight: 6 }} /> Check in with photo
                </Button>
              </div>
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "flex-end" }}>
            <StatBlock label="Type" value={landmarkCategoryLabel(landmark.category)} icon={landmarkCategoryIcon(landmark.category, 12)} />
            {isAuthenticated && <AddToListButton itemType="landmark" itemId={landmark.id} />}
          </div>
        </div>
      </div>

      {/* Location-mismatch notice: coral, informational, never an error state. */}
      {lastResult?.warning && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "var(--coral-500, #e5484d)",
          }}
        >
          <AlertTriangle size={15} />
          This photo doesn't look like it was taken here - it still counts.
        </div>
      )}

      {/* A trig pillar photographed at the top collects its peak too (spec 34). */}
      {lastResult?.peakCredited && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--status-success)" }}>
          <Mountain size={15} />
          <span>
            {lastResult.peakCredited.firstVisit ? "Also collected " : "Also logged a visit to "}
            <Link
              to="/peaks/$slug"
              params={{ slug: lastResult.peakCredited.slug }}
              style={{ color: "inherit", fontWeight: 600 }}
            >
              {lastResult.peakCredited.name}
            </Link>
            , the summit this pillar stands on.
          </span>
        </div>
      )}

      <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 20 }}>
        {/* your visits */}
        <AtlasPanel title="Your visits">
          {!isAuthenticated ? (
            <EmptyState
              icon={<Calendar size={18} />}
              title="Log in to track your visits"
              hint="Connect an activity source and Atlas logs this landmark automatically when your GPS track passes it - or check in with a photo when you're here."
              action={{ label: "Log in", onClick: () => void navigate({ to: "/login" }) }}
            />
          ) : visits.length === 0 ? (
            <EmptyState
              icon={<Flag size={18} />}
              title="Not visited yet"
              hint="Import an activity that passes this landmark, or check in with a photo - either logs your visit here."
            />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {visits.map((v) => (
                <div
                  key={v.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "10px 4px",
                    borderBottom: "1px solid var(--border-subtle)",
                  }}
                >
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)" }}>
                    {new Date(v.visitedAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </div>
                  {v.activityId && (
                    <Link
                      to="/activities/$id/review"
                      params={{ id: v.activityId }}
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: "var(--gold-400)",
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {v.activityName ?? "View activity"}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </AtlasPanel>

        {/* in collections */}
        <AtlasPanel title="In collections">
          {collections.length === 0 ? (
            <EmptyState icon={<Users size={18} />} title="Not part of any curated collection" />
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {collections.map((col) => (
                <CollectionCard
                  key={col.slug}
                  title={col.name}
                  type="Collection"
                  value={col.completedCount}
                  max={col.itemCount}
                  icon={<Flag size={22} />}
                  onClick={() => void navigate({ to: "/collections/$slug", params: { slug: col.slug } })}
                />
              ))}
            </div>
          )}
        </AtlasPanel>
      </div>

      {/* check-in gallery */}
      <CheckinGallery target={{ kind: "landmark", slug: landmark.slug }} isAuthenticated={isAuthenticated} />

      {/* nearby uncollected */}
      <AtlasPanel title="Nearby uncollected">
        {nearbyUncollected.length === 0 ? (
          <EmptyState icon={<MapPinned size={18} />} title="Nothing uncollected nearby" />
        ) : (
          <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10 }}>
            {nearbyUncollected.map((l) => (
              <CollectibleItem
                key={l.id}
                name={l.name}
                meta={
                  <>
                    {landmarkCategoryLabel(l.category)} · {l.distanceKm}km
                  </>
                }
                icon={landmarkCategoryIcon(l.category, 26)}
                onClick={() => void navigate({ to: "/landmarks/$slug", params: { slug: l.slug } })}
              />
            ))}
          </div>
        )}
      </AtlasPanel>

      {dialogOpen && (
        <CheckinDialog target={{ kind: "landmark", slug: landmark.slug }} onClose={() => setDialogOpen(false)} onSuccess={handleCheckinSuccess} />
      )}
    </div>
  );
}

export default function LandmarkDetailPage() {
  const params = useParams({ strict: false });
  const slug = (params as { slug?: string }).slug ?? "";
  const { user: me, isLoading: meLoading } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["landmarks", slug],
    queryFn: () => fetchLandmark(slug),
    enabled: !!slug,
    retry: false,
  });

  let content: React.ReactNode;

  if (isLoading || meLoading) {
    content = (
      <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
        <Skel height="200px" />
        <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 20 }}>
          <Skel height="220px" />
          <Skel height="220px" />
        </div>
        <Skel height="160px" />
      </div>
    );
  } else if (isError || !data) {
    content = (
      <div className="r-page" style={{ padding: 28, flex: 1, overflow: "auto" }}>
        <EmptyState icon={<Flag size={18} />} title="Landmark not found" />
      </div>
    );
  } else {
    content = <LandmarkContent data={data} isAuthenticated={!!me} />;
  }

  if (me) {
    return <AppShell>{content}</AppShell>;
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-app)", display: "flex", flexDirection: "column" }}>
      <GuestTopBar />
      <div className="atlas-guest-column">{content}</div>
    </div>
  );
}
